import { useLingui } from '@lingui/react/macro';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import { useCallback, useMemo } from 'react';
import { useDialogStore } from '@/components/dialogs';
import useAppStore from '../../../store/rootStore';
import { useWalletSignAuth } from '../../auth';

export function useDepositButtonText() {
  const { t } = useLingui();
  const isAtLeastConnected = useAppStore((state) => state.isAtLeastConnected());
  const needsAccountCreation = useAppStore((state) => state.status !== 'AUTHORIZED');
  const status = useAppStore((state) => state.status);
  const pendingAccessRequest = useAppStore((state) => state.pendingAccessRequest);

  return useMemo(() => {
    if (status === 'RESTRICTED') {
      return t({ comment: 'Restricted wallet button', message: 'Access Restricted' });
    }

    if (pendingAccessRequest) {
      return t({ comment: 'Enter access code button', message: 'Enter access code' });
    }

    if (status === 'AUTHORITY_BLOCKED') {
      return t({ comment: 'Geo-blocked action button', message: 'Unavailable in your location' });
    }

    if (!isAtLeastConnected) {
      return t({ comment: 'Connect wallet button', message: 'Connect' });
    }

    if (needsAccountCreation) {
      if (status === 'WALLET_CONNECTED') {
        return t({ comment: 'Sign in pending button', message: 'Sign in pending' });
      }

      return t({ comment: 'Enable trading button', message: 'Deposit to Enable trading' });
    }

    return t({
      comment:
        'Deposit collateral button. An action ("add funds"), not the noun for a deposit transaction — the uncontexted "Deposit" is the noun used by the account-history row and the table filter.',
      context: 'action',
      message: 'Deposit',
    });
  }, [isAtLeastConnected, needsAccountCreation, t, status, pendingAccessRequest]);
}

/** The click behind `useDepositButtonText`'s label. */
export function useDepositButtonAction() {
  const { openConnectModal } = useConnectModal();
  const { agreeAndSign } = useWalletSignAuth();

  return useCallback(() => {
    const state = useAppStore.getState();

    if (state.status === 'AUTHORITY_BLOCKED') {
      useDialogStore.getState().openDialog('geoBlockDialog', { countryCode: state.config?.userCountry ?? 'us' });
    } else if (state.pendingAccessRequest) {
      useDialogStore.getState().openDialog('accessRequestDialog');
    } else if (state.isAtLeastAuthenticated()) {
      useDialogStore.getState().openDialog('depositCollateralDialog');
    } else if (state.isAtLeastConnected()) {
      agreeAndSign().catch((error) => {
        console.warn('Failed to agree and sign:', error);
      });
    } else {
      openConnectModal?.();
    }
  }, [agreeAndSign, openConnectModal]);
}
